// Small utility: 3rd-degree polynomial regression via normal equations.
// Enough data density for ~5-15 lactate points; we solve [A^T A] c = A^T y with Gaussian elimination.

function solveLinearSystem(A, b) {
  // Augment
  const n = A.length;
  const m = A[0].length;
  const M = A.map((row, i) => [...row, b[i]]);
  // Forward elimination with partial pivoting
  for (let col = 0; col < m; col++) {
    let pivot = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(M[r][col]) > Math.abs(M[pivot][col])) pivot = r;
    }
    if (pivot !== col) {
      const tmp = M[col];
      M[col] = M[pivot];
      M[pivot] = tmp;
    }
    if (Math.abs(M[col][col]) < 1e-12) return null; // singular
    for (let r = col + 1; r < n; r++) {
      const factor = M[r][col] / M[col][col];
      for (let c = col; c <= m; c++) M[r][c] -= factor * M[col][c];
    }
  }
  // Back substitution
  const x = new Array(m).fill(0);
  for (let i = m - 1; i >= 0; i--) {
    let sum = M[i][m];
    for (let j = i + 1; j < m; j++) sum -= M[i][j] * x[j];
    x[i] = sum / M[i][i];
  }
  return x;
}

/**
 * Fit y ≈ c0 + c1*x + c2*x^2 + c3*x^3 (default degree 3).
 * Returns {coefs, predict(x)} or null if there aren't enough points.
 */
export function polynomialRegression(points, degree = 3) {
  const pts = points.filter(
    (p) => Number.isFinite(p.x) && Number.isFinite(p.y)
  );
  if (pts.length < degree + 1) return null;
  const k = degree + 1;
  // Design matrix A (n x k) where A[i][j] = x_i^j
  const A = pts.map((p) => {
    const row = new Array(k);
    let v = 1;
    for (let j = 0; j < k; j++) {
      row[j] = v;
      v *= p.x;
    }
    return row;
  });
  const y = pts.map((p) => p.y);
  // Normal equations: (A^T A) c = A^T y
  const AT = Array.from({ length: k }, (_, i) => A.map((row) => row[i]));
  const ATA = AT.map((rowI) =>
    AT.map((rowJ) => rowI.reduce((s, _, idx) => s + rowI[idx] * rowJ[idx], 0))
  );
  const ATy = AT.map((rowI) => rowI.reduce((s, v, idx) => s + v * y[idx], 0));
  const coefs = solveLinearSystem(ATA, ATy);
  if (!coefs) return null;
  return {
    coefs,
    predict: (x) => {
      let v = 1;
      let sum = 0;
      for (let j = 0; j < coefs.length; j++) {
        sum += coefs[j] * v;
        v *= x;
      }
      return sum;
    },
  };
}
